import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibDateField } from '@chewy/kib-calendar-react'
import { KibField, KibSelect } from '@chewy/kib-fields-new-react'
import { KibInputGroup } from '@chewy/kib-fields-react'
import { Grid } from '@mui/material'
import { moment, Utils } from '@pbt/pbt-ui-components'

import {
  AdminFrequencyToTaskFrequencyMap,
  RepeatMode,
  RepeatModeList,
} from '~/constants/taskConstants'
import {
  getDrugAdministrationFrequencies,
  getTaskTimeUnits,
} from '~/store/reducers/constants'
import type { DataHandle, Prescription } from '~/types'
import useEffectExceptOnMount from '~/utils/useEffectExceptOnMount'

import '@chewy/kib-calendar-styles/dist/kib-calendar.css'
import style from './SwapProductTaskDetails.module.scss'

const HOURS = ['12', ...Array.from({ length: 11 }, (_, i) => String(i + 1))] // 12-11 hours
const MINUTES = Array.from({ length: 12 }, (_, i) =>
  String(i * 5).padStart(2, '0'),
) // 00-55 minutes
const TIME_PERIODS = ['AM', 'PM']

const initializeTaskState = (
  prescription: Prescription,
  drugAdministrationFrequencies: Array<{ id: string; name: string }>,
) => {
  const existingTask = prescription.task

  const adminFrequency = drugAdministrationFrequencies.find(
    (freq) => freq.id === prescription.administrationFrequencyId,
  )
  const adminFrequencyName = adminFrequency?.name || ''

  const taskName = existingTask?.name || `Administer ${prescription.name}`

  const taskDueDate = existingTask?.dueDatetime
    ? moment(existingTask.dueDatetime).format('MM/DD/YYYY')
    : ''

  const existingTime = existingTask?.dueDatetime
    ? new Date(existingTask.dueDatetime)
    : null

  const taskHour = existingTime
    ? String(existingTime.getHours() % 12 || 12)
    : HOURS[0] || '12'

  const taskMinutes = existingTime
    ? String(Math.floor(existingTime.getMinutes() / 5) * 5).padStart(2, '0')
    : MINUTES[0] || '00'

  const taskPeriod = existingTime
    ? existingTime.getHours() >= 12
      ? 'PM'
      : 'AM'
    : TIME_PERIODS[0] || 'AM'

  const suggestedFrequency =
    AdminFrequencyToTaskFrequencyMap[adminFrequencyName]
  const hasExistingRecurrence =
    existingTask?.recurrencePeriod?.amount &&
    existingTask?.recurrencePeriod?.unit
  const repeatMode =
    hasExistingRecurrence || suggestedFrequency
      ? RepeatMode.REPEAT
      : RepeatMode.DOES_NOT_REPEAT

  return {
    taskName,
    taskDueDate,
    taskHour,
    taskMinutes,
    taskPeriod,
    repeatMode,
  }
}

/**
 * Converts 12-hour format to 24-hour format
 */
const convertTo24Hour = (hour: string, period: string): number => {
  const hourNum = parseInt(hour, 10)

  if (period === 'PM' && hour !== '12') {
    return hourNum + 12
  }

  if (period === 'AM' && hour === '12') {
    return 0
  }

  return hourNum
}

const combineDateAndTime = (
  date: string,
  hour: string,
  minutes: string,
  period: string,
): string | null => {
  const datePart = moment(date, 'MM/DD/YYYY', true) // strict parsing

  if (!datePart.isValid()) {
    return null
  }

  const hour24 = convertTo24Hour(hour, period)
  return datePart
    .set({ hour: hour24, minute: parseInt(minutes, 10), second: 0 })
    .toISOString()
}

const calculateRecurrenceEndDate = (
  startDate: string,
  courseLengthDays: number,
): string => moment(startDate).add(courseLengthDays, 'days').toISOString()

export interface SwapProductTaskDetailsProps {
  onRefChange?: () => void
  prescription: Prescription
}

export interface SwapProductTaskDetailsHandle extends DataHandle {}

const SwapProductTaskDetails = forwardRef<
  SwapProductTaskDetailsHandle,
  SwapProductTaskDetailsProps
>(function SwapProductTaskDetails({ onRefChange, prescription }, ref) {
  const { t } = useTranslation(['Common', 'Constants', 'Tasks'])

  const taskTimeUnits = useSelector(getTaskTimeUnits)
  const drugAdministrationFrequencies = useSelector(
    getDrugAdministrationFrequencies,
  )

  const timeUnitIds = useMemo(
    () => ({
      hour: Utils.findConstantIdByName('Hour', taskTimeUnits),
      day: Utils.findConstantIdByName('Day', taskTimeUnits),
      week: Utils.findConstantIdByName('Week', taskTimeUnits),
      month: Utils.findConstantIdByName('Month', taskTimeUnits),
    }),
    [taskTimeUnits],
  )

  const [taskName, setTaskName] = useState<string>(
    () =>
      initializeTaskState(prescription, drugAdministrationFrequencies).taskName,
  )
  const [taskDueDate, setTaskDueDate] = useState<string>(
    () =>
      initializeTaskState(prescription, drugAdministrationFrequencies)
        .taskDueDate,
  )
  const [taskHour, setTaskHour] = useState<string>(
    () =>
      initializeTaskState(prescription, drugAdministrationFrequencies).taskHour,
  )
  const [taskMinutes, setTaskMinutes] = useState<string>(
    () =>
      initializeTaskState(prescription, drugAdministrationFrequencies)
        .taskMinutes,
  )
  const [taskPeriod, setTaskPeriod] = useState<string>(
    () =>
      initializeTaskState(prescription, drugAdministrationFrequencies)
        .taskPeriod,
  )
  const [repeatMode, setRepeatMode] = useState<string>(
    () =>
      initializeTaskState(prescription, drugAdministrationFrequencies)
        .repeatMode,
  )

  const adminFrequency = drugAdministrationFrequencies.find(
    (freq) => freq.id === prescription.administrationFrequencyId,
  )
  const adminFrequencyName = adminFrequency?.name || ''
  const suggestedFrequency =
    AdminFrequencyToTaskFrequencyMap[adminFrequencyName]
  const existingTask = prescription.task

  const getRecurrencePeriodAmount = (): number => {
    if (
      existingTask?.recurrencePeriod?.amount &&
      existingTask?.recurrencePeriod?.unit
    ) {
      return existingTask.recurrencePeriod.amount
    }
    if (suggestedFrequency) {
      return suggestedFrequency.amount
    }
    return 1
  }

  useImperativeHandle(
    ref,
    () => ({
      get: () => {
        const dueDate = combineDateAndTime(
          taskDueDate,
          taskHour,
          taskMinutes,
          taskPeriod,
        )

        if (repeatMode === RepeatMode.DOES_NOT_REPEAT) {
          return {
            name: taskName,
            dueDate: dueDate!,
          }
        }

        const recurrencePeriodAmount = getRecurrencePeriodAmount()
        let recurrencePeriodUnitId = timeUnitIds.day

        if (
          existingTask?.recurrencePeriod?.amount &&
          existingTask?.recurrencePeriod?.unit
        ) {
          recurrencePeriodUnitId = existingTask.recurrencePeriod.unit
        } else if (suggestedFrequency) {
          const unitNameToIdMap: Record<string, string> = {
            Hour: timeUnitIds.hour,
            Day: timeUnitIds.day,
            Week: timeUnitIds.week,
            Month: timeUnitIds.month,
          }
          recurrencePeriodUnitId =
            unitNameToIdMap[suggestedFrequency.unit] ?? timeUnitIds.day
        }

        const courseLengthDays = prescription.administrationCourseLength || 1 // Default to 1 day if not set
        const recurrenceEndDatetime = calculateRecurrenceEndDate(
          dueDate!,
          courseLengthDays,
        )

        return {
          name: taskName,
          dueDate: dueDate!,
          recurrenceEndDatetime,
          recurrencePeriod: {
            amount: recurrencePeriodAmount,
            unit: recurrencePeriodUnitId,
          },
        }
      },
      validate: () => {
        if (!taskName) return false
        if (!taskDueDate) return false

        const dueDate = combineDateAndTime(
          taskDueDate,
          taskHour,
          taskMinutes,
          taskPeriod,
        )
        if (!dueDate) {
          return false
        }

        if (repeatMode === RepeatMode.REPEAT) {
          const recurrencePeriodAmount = getRecurrencePeriodAmount()

          if (recurrencePeriodAmount <= 0) {
            return false
          }
        }

        return true
      },
    }),
    [
      taskName,
      taskDueDate,
      taskHour,
      taskMinutes,
      taskPeriod,
      repeatMode,
      timeUnitIds,
      prescription,
      drugAdministrationFrequencies,
    ],
  )

  useEffectExceptOnMount(() => {
    const newState = initializeTaskState(
      prescription,
      drugAdministrationFrequencies,
    )
    setTaskName(newState.taskName)
    setTaskDueDate(newState.taskDueDate)
    setTaskHour(newState.taskHour)
    setTaskMinutes(newState.taskMinutes)
    setTaskPeriod(newState.taskPeriod)
    setRepeatMode(newState.repeatMode)
  }, [
    prescription.task,
    prescription.name,
    prescription.administrationFrequencyId,
    drugAdministrationFrequencies,
  ])

  useEffect(() => {
    onRefChange?.()
  }, [
    taskName,
    taskDueDate,
    taskHour,
    taskMinutes,
    taskPeriod,
    repeatMode,
    onRefChange,
  ])

  return (
    <Grid container className={style.taskDetailsContainer} direction="column">
      <KibInputGroup
        label={t('Dialogs:PRESCRIPTION_DIALOG.PET_PARENT_TASK_DETAILS')}
      >
        <KibField
          className={style.taskName}
          label={t('Tasks:LABEL.TASK_NAME')}
          value={taskName}
          onChange={(e) => setTaskName(e.target.value)}
        />
        <KibSelect
          className={style.repeatSelect}
          label={t('Common:REPEAT_ACTION')}
          value={repeatMode}
          onChange={(e) => setRepeatMode(e.target.value)}
        >
          {RepeatModeList.map((mode) => (
            <option key={mode.id} value={mode.id}>
              {mode.name}
            </option>
          ))}
        </KibSelect>
        <div className={style.taskDateTime}>
          <KibDateField
            showCalendar
            format="MM/dd/yyyy"
            help=""
            label={t('Tasks:LABEL.DUE_DATE')}
            placeholder="mm/dd/yyyy"
            value={taskDueDate}
            onChange={(e) => setTaskDueDate(e.target.value)}
          />
          <span className={style.taskTime}>
            <KibSelect
              label={t('Constants:TIME_UNITS.HOUR_ONE')}
              value={taskHour}
              onChange={(e) => setTaskHour(e.target.value)}
            >
              {HOURS.map((hour) => (
                <option key={hour} value={hour}>
                  {hour}
                </option>
              ))}
            </KibSelect>
            <KibSelect
              label={t('Constants:TIME_UNITS.MINUTE_ONE')}
              value={taskMinutes}
              onChange={(e) => setTaskMinutes(e.target.value)}
            >
              {MINUTES.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {minutes}
                </option>
              ))}
            </KibSelect>
            <KibSelect
              hiddenLabel
              className={style.taskTimePeriod}
              label={t('Common:PERIOD')}
              value={taskPeriod}
              onChange={(e) => setTaskPeriod(e.target.value)}
            >
              {TIME_PERIODS.map((period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </KibSelect>
          </span>
        </div>
      </KibInputGroup>
    </Grid>
  )
})

export default SwapProductTaskDetails
